import { z } from 'zod';
import { UuidSchema } from './primitives/uuid.js';
// ============================================================================
// Admin Database Schemas
// ============================================================================

// Ping schemas
export const AdminDatabasePingQuerySchema = z.object({
  full: z.coerce.boolean().optional(),
  samples: z.coerce.number().int().min(1).max(100).optional(),
});

export type AdminDatabasePingQuery = z.infer<typeof AdminDatabasePingQuerySchema>;

export const AdminDatabasePingResponseSchema = z.object({
  latency: z.number().optional(),
  timestamp: z.date(),
  region: z.string().nullable(),
  stats: z
    .object({
      samples: z.array(z.number()),
      sampleCount: z.number().int(),
      min: z.number(),
      max: z.number(),
      median: z.number(),
      p75: z.number(),
      p90: z.number(),
      p95: z.number(),
      p99: z.number(),
      mean: z.number(),
      stdDev: z.number(),
    })
    .optional(),
  queryTimings: z.record(z.string(), z.number()).optional(),
  measurement: z.string().optional(),
  note: z.string().optional(),
  prepared: z
    .object({
      firstMs: z.number(),
      warmMedianMs: z.number(),
      deltaMs: z.number(),
    })
    .optional(),
  rttApproxMs: z.number().optional(),
});

export type AdminDatabasePingResponse = z.infer<typeof AdminDatabasePingResponseSchema>;

// Query schemas
export const AdminDatabaseQueryBodySchema = z.object({
  sql: z.string().min(1).max(20_000),
  parameters: z.array(z.unknown()).max(64).optional(),
  simulate: z.boolean().optional(),
  maxRows: z.number().int().positive().max(2000).optional(),
});

export type AdminDatabaseQueryBody = z.infer<typeof AdminDatabaseQueryBodySchema>;

export const AdminDatabaseQueryFieldSchema = z.object({
  name: z.string(),
  typeId: z.number().nullable(),
  typeModifier: z.number().nullable(),
});

export type AdminDatabaseQueryField = z.infer<typeof AdminDatabaseQueryFieldSchema>;

export const AdminDatabaseQueryResultSchema = z.object({
  commandTag: z.string().nullable(),
  rowCount: z.number().int(),
  rows: z.array(z.record(z.string(), z.unknown())),
  fields: z.array(AdminDatabaseQueryFieldSchema),
  truncated: z.boolean(),
  durationMs: z.number(),
  executedAt: z.date(),
  simulated: z.boolean(),
  maxRows: z.number().int(),
  notices: z.array(z.unknown()),
});

export type AdminDatabaseQueryResult = z.infer<typeof AdminDatabaseQueryResultSchema>;

export const AdminDatabaseQueryResponseSchema = z.object({
  result: AdminDatabaseQueryResultSchema,
});

export type AdminDatabaseQueryResponse = z.infer<typeof AdminDatabaseQueryResponseSchema>;

export const AdminDatabaseStatsResponseSchema = z.object({
  database: z.object({
    sizePretty: z.string(),
    sizeBytes: z.number(),
    tableCount: z.number(),
    totalLiveRows: z.number(),
    totalDeadRows: z.number(),
    totalInserts: z.number(),
    totalUpdates: z.number(),
    totalDeletes: z.number(),
    avgBloatPct: z.number(),
  }),
  tables: z.array(
    z.object({
      tableName: z.string(),
      sizePretty: z.string(),
      sizeBytes: z.number(),
      liveRows: z.number(),
      deadRows: z.number(),
      bloatPct: z.number(),
      totalInserts: z.number(),
      totalUpdates: z.number(),
      totalDeletes: z.number(),
      hotUpdates: z.number(),
      hotUpdatePct: z.number(),
      lastVacuum: z.string().nullable(),
      lastAutovacuum: z.string().nullable(),
      lastAnalyze: z.string().nullable(),
      lastAutoanalyze: z.string().nullable(),
      seqScans: z.number(),
      indexScans: z.number(),
      indexUsagePct: z.number(),
    })
  ),
});

export type AdminDatabaseStatsResponse = z.infer<typeof AdminDatabaseStatsResponseSchema>;

// ============================================================================
// Admin Fetch Sessions
// ============================================================================

export const AdminFetchSessionSourceSchema = z.enum(['home_timeline', 'user_timeline', 'search', 'custom']);

export const AdminFetchSessionResultsSchema = z.object({
  newTweets: z.number().int(),
  updatedTweets: z.number().int(),
  newUsers: z.number().int(),
  updatedUsers: z.number().int(),
  alreadyHad: z.number().int(),
  errors: z.number().int(),
});

export const AdminFetchSessionMetadataSchema = z
  .object({
    duration: z.number().nullable().optional(),
    endpoint: z.string().nullable().optional(),
    sinceId: z.string().nullable().optional(),
    untilId: z.string().nullable().optional(),
  })
  .partial();

export const AdminFetchSessionSchema = z.object({
  id: z.string(),
  source: AdminFetchSessionSourceSchema,
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  requested: z.number().int(),
  username: z.string().nullable().optional(),
  searchQuery: z.string().nullable().optional(),
  results: AdminFetchSessionResultsSchema,
  metadata: AdminFetchSessionMetadataSchema.optional(),
});

export const AdminFetchSessionsResponseSchema = z.array(AdminFetchSessionSchema);

export type AdminFetchSession = z.infer<typeof AdminFetchSessionSchema>;
export type AdminFetchSessionsResponse = z.infer<typeof AdminFetchSessionsResponseSchema>;

// ============================================================================
// Admin Queue Stats Schemas
// ============================================================================

export const AdminQueueStatsEntrySchema = z.object({
  queue: z.string(),
  displayName: z.string(),
  pending: z.number().int(),
  processing: z.number().int(),
  completed: z.number().int(),
  failed: z.number().int(),
  concurrency: z.number().int().nullable().optional(),
  throttle: z.unknown().nullable().optional(),
  avgProcessingTime: z.number().optional(),
});

export type AdminQueueStatsEntry = z.infer<typeof AdminQueueStatsEntrySchema>;

export const AdminQueueStatsResponseSchema = z.object({
  stats: z.array(AdminQueueStatsEntrySchema),
});

export type AdminQueueStatsResponse = z.infer<typeof AdminQueueStatsResponseSchema>;

// ============================================================================
// Admin Logs Schemas
// ============================================================================

export const AdminLogLevelSchema = z.enum(['trace', 'debug', 'info', 'warn', 'error']);

export type AdminLogLevel = z.infer<typeof AdminLogLevelSchema>;

export const AdminLogsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(500).optional(),
  level: AdminLogLevelSchema.optional(),
  levels: z.array(AdminLogLevelSchema).optional(),
  since: z.string().min(1).optional(),
  search: z.string().min(1).optional(),
  source: z.string().min(1).optional(),
  sources: z.array(z.string().min(1)).optional(),
});

export type AdminLogsQuery = z.infer<typeof AdminLogsQuerySchema>;

export const AdminLogsResponseSchema = z.object({
  data: z.array(z.unknown()),
  pagination: z
    .object({
      next: z.string().nullable(),
      previous: z.string().nullable(),
    })
    .optional(),
});

export type AdminLogsResponse = z.infer<typeof AdminLogsResponseSchema>;

// Single log entry
export const AdminLogEntryPathSchema = z.object({
  logId: z.string().min(1),
});

export type AdminLogEntryPath = z.infer<typeof AdminLogEntryPathSchema>;

export const AdminLogEntryResponseSchema = z.unknown();

export type AdminLogEntryResponse = z.infer<typeof AdminLogEntryResponseSchema>;

// Test log
export const AdminLogsTestBodySchema = z.object({
  level: AdminLogLevelSchema.default('info'),
  message: z.string().min(1).max(500),
  context: z.record(z.string(), z.unknown()).optional(),
});

export type AdminLogsTestBody = z.infer<typeof AdminLogsTestBodySchema>;

// ============================================================================
// Admin Rate Limits Schemas
// ============================================================================

export const AdminRateLimitStatusSchema = z.object({
  endpoint: z.string(),
  description: z.string(),
  proLimit: z.number(),
  proWindow: z.string(),
  currentLimit: z.number().nullable(),
  remaining: z.number().nullable(),
  resetAt: z.date().nullable(),
  lastRequestAt: z.date().nullable(),
  lastRateLimitErrorAt: z.date().nullable(),
  consecutiveRateLimitErrors: z.number(),
  status: z.enum(['healthy', 'warning', 'critical', 'unknown']),
  usagePercent: z.number(),
  timeToReset: z.number().optional(),
  isStale: z.boolean(),
});

export const AdminRateLimitSummarySchema = z.object({
  totalEndpoints: z.number(),
  healthy: z.number(),
  warning: z.number(),
  critical: z.number(),
  unknown: z.number(),
  lastUpdated: z.date(),
});

export const AdminRateLimitsResponseSchema = z.object({
  summary: AdminRateLimitSummarySchema,
  endpoints: z.array(AdminRateLimitStatusSchema),
});

export const AdminRateLimitProbeResponseSchema = z.object({
  type: z.literal('probe'),
  message: z.string(),
  endpoint: z.string(),
});

export const AdminRateLimitResetResponseSchema = z.object({
  type: z.literal('reset'),
  message: z.string(),
  resetCount: z.number(),
  endpoints: z.array(z.string()),
});

export const AdminRateLimitRefreshResponseSchema = z.union([
  AdminRateLimitProbeResponseSchema,
  AdminRateLimitResetResponseSchema,
]);

export type AdminRateLimitStatus = z.infer<typeof AdminRateLimitStatusSchema>;
export type AdminRateLimitSummary = z.infer<typeof AdminRateLimitSummarySchema>;
export type AdminRateLimitsResponse = z.infer<typeof AdminRateLimitsResponseSchema>;
export type AdminRateLimitRefreshResponse = z.infer<typeof AdminRateLimitRefreshResponseSchema>;

export const AdminLogsTestResponseSchema = z.object({
  message: z.string(),
  level: AdminLogLevelSchema,
});

export type AdminLogsTestResponse = z.infer<typeof AdminLogsTestResponseSchema>;

// ============================================================================
// Admin Log Level Schemas
// ============================================================================

export const AdminLogLevelValueSchema = z.enum(['trace', 'debug', 'info', 'warn', 'error', 'fatal', 'silent']);

export type AdminLogLevelValue = z.infer<typeof AdminLogLevelValueSchema>;

export const AdminLogLevelGetResponseSchema = z.object({
  level: AdminLogLevelValueSchema,
});

export type AdminLogLevelGetResponse = z.infer<typeof AdminLogLevelGetResponseSchema>;

export const AdminLogLevelSetBodySchema = z.object({
  level: AdminLogLevelValueSchema,
});

export type AdminLogLevelSetBody = z.infer<typeof AdminLogLevelSetBodySchema>;

export const AdminLogLevelSetResponseSchema = z.object({
  message: z.string(),
  level: AdminLogLevelValueSchema,
});

export type AdminLogLevelSetResponse = z.infer<typeof AdminLogLevelSetResponseSchema>;

// Twitter Access Mode
export const TwitterAccessModeSchema = z.enum(['twitter', 'graphql', 'hybrid']);
export type TwitterAccessMode = z.infer<typeof TwitterAccessModeSchema>;

export const AdminTwitterAccessGetResponseSchema = z.object({
  defaultMode: TwitterAccessModeSchema,
  globalOverride: TwitterAccessModeSchema.nullable(),
  effectiveMode: TwitterAccessModeSchema,
});

export type AdminTwitterAccessGetResponse = z.infer<typeof AdminTwitterAccessGetResponseSchema>;

export const AdminTwitterAccessUpdateBodySchema = z.object({
  mode: TwitterAccessModeSchema.nullable(),
});

export type AdminTwitterAccessUpdateBody = z.infer<typeof AdminTwitterAccessUpdateBodySchema>;

// Debug Settings
export const AdminDebugSettingsQuerySchema = z.object({
  userId: UuidSchema,
});

export type AdminDebugSettingsQuery = z.infer<typeof AdminDebugSettingsQuerySchema>;

export const AdminDebugSettingsResponseSchema = z.object({
  betaFeatures: z.object({
    debugLogging: z.boolean(),
  }),
  role: z.enum(['free', 'pro', 'admin']),
});

export type AdminDebugSettingsResponse = z.infer<typeof AdminDebugSettingsResponseSchema>;

export const AdminDebugSettingsUpdateBodySchema = z.object({
  debugLogging: z.boolean(),
});

export type AdminDebugSettingsUpdateBody = z.infer<typeof AdminDebugSettingsUpdateBodySchema>;

// ============================================================================
// Admin Tweets Schemas
// ============================================================================

export const AdminTweetLookupQuerySchema = z.object({
  id: z.string().regex(/^\d+$/, 'Tweet ID must be numeric'),
});

export type AdminTweetLookupQuery = z.infer<typeof AdminTweetLookupQuerySchema>;

export const AdminTweetLookupResponseSchema = z.object({
  tweet: z.unknown().nullable(),
  author: z.unknown().nullable(),
  referencedTweets: z.unknown().nullable(),
  metadata: z.object({
    fetchedAt: z.date(),
    hasMedia: z.boolean(),
    hasPoll: z.boolean(),
    hasCard: z.boolean(),
    isReply: z.boolean(),
    isRetweet: z.boolean(),
    isQuote: z.boolean(),
  }),
  message: z.string().optional(),
});

export type AdminTweetLookupResponse = z.infer<typeof AdminTweetLookupResponseSchema>;

export const AdminTweetRefreshBodySchema = z.object({
  id: z.string().regex(/^\d+$/, 'Tweet ID must be numeric'),
});

export type AdminTweetRefreshBody = z.infer<typeof AdminTweetRefreshBodySchema>;

export const AdminTweetRefreshResponseSchema = z.object({
  success: z.boolean(),
  tweetId: z.string(),
  counts: z.object({
    tweets: z.number().int(),
    users: z.number().int(),
    media: z.number().int(),
  }),
  timestamp: z.date(),
});

export type AdminTweetRefreshResponse = z.infer<typeof AdminTweetRefreshResponseSchema>;

// ============================================================================
// Admin System Schemas
// ============================================================================

// Activities
export const AdminActivitiesQuerySchema = z.object({
  includeEvents: z.coerce.boolean().optional(),
  activityId: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
  userId: z.string().optional(),
  status: z.string().optional(),
  type: z.string().optional(),
  page: z.coerce.number().int().min(1).optional().default(1),
});

export type AdminActivitiesQuery = z.infer<typeof AdminActivitiesQuerySchema>;

export const AdminActivitiesResponseSchema = z.object({
  activities: z.array(z.unknown()),
  events: z.array(z.unknown()).optional(),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
});

export type AdminActivitiesResponse = z.infer<typeof AdminActivitiesResponseSchema>;

// Environment Mode
export const AdminEnvModeResponseSchema = z.object({
  mode: z.string().nullable(),
  vercelEnv: z.string().nullable(),
  nodeEnv: z.string().nullable(),
  hasInngestAdmin: z.boolean(),
  isProdLike: z.boolean(),
});

export type AdminEnvModeResponse = z.infer<typeof AdminEnvModeResponseSchema>;

// Missing Users
export const AdminMissingUsersStatsSchema = z.object({
  missingCount: z.number().int(),
  totalJobs: z.number().int(),
  processing: z.number().int(),
  failed: z.number().int(),
  lastDetectionRun: z.date().nullable(),
  breakdown: z.object({
    fromTweets: z.number().int(),
    fromRelationships: z.number().int(),
    fromFollowerEvents: z.number().int(),
    placeholderProfiles: z.number().int(),
    pending: z.number().int(),
    pendingResolutionRequests: z.number().int(),
  }),
});

export type AdminMissingUsersStats = z.infer<typeof AdminMissingUsersStatsSchema>;

export const AdminMissingUsersJobSchema = z.object({
  id: z.string(),
  status: z.string(),
  createdAt: z.date(),
  completedAt: z.date().nullable(),
  lastError: z.string().nullable(),
  estimatedTotal: z.number().int().nullable(),
  progressCount: z.number().int(),
  metadata: z
    .object({
      totalUsers: z.number().int().optional(),
      limit: z.number().int().optional(),
      profileIdsCount: z.number().int().optional(),
    })
    .nullable(),
});

export type AdminMissingUsersJob = z.infer<typeof AdminMissingUsersJobSchema>;

export const AdminMissingUsersGetResponseSchema = z.object({
  stats: AdminMissingUsersStatsSchema,
  jobs: z.array(AdminMissingUsersJobSchema),
  sampleUsers: z.array(z.string()),
});

export type AdminMissingUsersGetResponse = z.infer<typeof AdminMissingUsersGetResponseSchema>;

export const AdminMissingUsersActionBodySchema = z.object({
  action: z.enum(['trigger_detection', 'retry_failed_jobs', 'cleanup_failed_jobs', 'resolve_now']),
  windowMinutes: z.number().int().min(1).max(999_999).optional(),
  limit: z.number().int().min(1).max(2000).optional(),
});

export type AdminMissingUsersActionBody = z.infer<typeof AdminMissingUsersActionBodySchema>;

export const AdminMissingUsersActionResponseSchema = z.object({
  message: z.string(),
  stats: AdminMissingUsersStatsSchema,
});

export type AdminMissingUsersActionResponse = z.infer<typeof AdminMissingUsersActionResponseSchema>;

// ============================================================================
// Admin Debug Schemas
// ============================================================================

export const AdminDebugClearCachesBodySchema = z.object({
  userId: UuidSchema,
});

export type AdminDebugClearCachesBody = z.infer<typeof AdminDebugClearCachesBodySchema>;

export const AdminDebugClearCachesResponseSchema = z.object({
  success: z.literal(true),
  cleared: z.number().int(),
  details: z.object({
    timeline: z.number().int(),
  }),
});

export type AdminDebugClearCachesResponse = z.infer<typeof AdminDebugClearCachesResponseSchema>;

// ============================================================================
// Admin AI Usage Schemas
// ============================================================================

const AiUsageQuotaEntrySchema = z.object({
  ok: z.boolean(),
  remaining: z.number(),
  limit: z.number(),
  used: z.number(),
  resetDate: z.date().optional(),
});

export const AdminAiUsageOverviewSchema = z.object({
  totalRequests: z.number().optional(),
  totalCostCredits: z.number().optional(),
  totalTokens: z.number().optional(),
  avgResponseTime: z.number().optional(),
  timeRange: z.string(),
});

export const AdminAiUsageFeatureMetricSchema = z
  .object({
    feature: z.string(),
    totalRequests: z.number(),
    avgResponseTime: z.number(),
    totalCostCredits: z.number(),
    successRate: z.number(),
    totalCostCents: z.number(),
  })
  .nullable();

export const AdminAiUsageModelStatSchema = z.object({
  model: z.string(),
  totalRequests: z.number(),
  totalCostCredits: z.number(),
  totalTokens: z.number(),
  avgTokensPerRequest: z.number(),
});

export const AdminAiUsageDailyStatSchema = z.object({
  date: z.string(),
  totalRequests: z.number(),
  totalCostCredits: z.number(),
  totalTokens: z.number(),
});

export const AdminAiUsageRecentRequestSchema = z.object({
  id: z.string(),
  model: z.string().nullable(),
  feature: z.string().nullable(),
  inputTokens: z.number().nullable(),
  outputTokens: z.number().nullable(),
  totalTokens: z.number().nullable(),
  costCredits: z.number().nullable(),
  success: z.boolean().nullable(),
  responseTime: z.number(),
  createdAt: z.date(),
  errorMessage: z.string().nullable(),
  status: z.enum(['success', 'failed']),
  promptTokens: z.number().nullable(),
  completionTokens: z.number().nullable(),
});

export const AdminAiUsageModelPricingSchema = z.object({
  model: z.string(),
  inputCostPerMillionUSD: z.number(),
  outputCostPerMillionUSD: z.number(),
  maxTokens: z.number().nullable(),
  contextWindow: z.number().nullable(),
  isActive: z.boolean(),
});

export const AdminAiUsageTopUserSchema = z.object({
  userId: z.string().nullable(),
  username: z.string().nullable(),
  name: z.string().nullable(),
  profileImageUrl: z.string().nullable(),
  totalRequests: z.number(),
  totalCostCredits: z.number(),
  totalTokens: z.number(),
  premiumRequests: z.number(),
  premiumCostCredits: z.number(),
  premiumTokens: z.number(),
  standardRequests: z.number(),
  standardCostCredits: z.number(),
  standardTokens: z.number(),
  quotaUsage: z
    .object({
      ai_requests_standard: AiUsageQuotaEntrySchema.optional(),
      ai_requests_premium: AiUsageQuotaEntrySchema.optional(),
    })
    .nullable(),
  entitlements: z
    .object({
      limits: z.record(z.string(), z.unknown()),
      features: z.record(z.string(), z.unknown()),
    })
    .nullable(),
});

export const AdminAiUsageResponseSchema = z.object({
  overview: AdminAiUsageOverviewSchema,
  featureMetrics: z.object({
    timeline: AdminAiUsageFeatureMetricSchema,
    userAnalysis: AdminAiUsageFeatureMetricSchema,
    arenaAnalysis: AdminAiUsageFeatureMetricSchema,
    summarize: AdminAiUsageFeatureMetricSchema,
  }),
  modelStats: z.array(AdminAiUsageModelStatSchema),
  dailyUsage: z.array(AdminAiUsageDailyStatSchema),
  recentRequests: z.array(AdminAiUsageRecentRequestSchema),
  modelPricing: z.array(AdminAiUsageModelPricingSchema),
  topUsers: z.array(AdminAiUsageTopUserSchema),
  quotaStats: z.array(z.unknown()),
});

export type AdminAiUsageResponse = z.infer<typeof AdminAiUsageResponseSchema>;
